import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { authAPI } from '../../api/auth';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    pin: '',
    confirmPin: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 验证密码匹配
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    // 验证密码长度
    if (formData.password.length < 6) {
      setError('密码长度至少为6位');
      return;
    }

    // 验证PIN码（6位数字）
    if (!formData.pin || formData.pin.length !== 6) {
      setError('PIN码必须为6位数字');
      return;
    }

    if (!/^\d+$/.test(formData.pin)) {
      setError('PIN码只能包含数字');
      return;
    }

    if (formData.pin !== formData.confirmPin) {
      setError('两次输入的PIN码不一致');
      return;
    }

    setLoading(true);

    try {
      const registerData: any = {
        username: formData.username && formData.username.trim() ? formData.username.trim() : undefined,  // 如果为空则不传递
        email: formData.email,
        password: formData.password,
        pin: formData.pin,
      };

      const response = await authAPI.register(registerData);
      localStorage.setItem('access_token', response.access_token);
      // 触发自定义事件，通知其他组件登录状态已改变
      window.dispatchEvent(new Event('localStorageChange'));
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || '注册失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center">
            <img src="/2025.png" alt="Logo" className="w-16 h-16 mx-auto mb-4 object-contain" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">猫爪AI工作社</h2>
            <p className="text-gray-600">创建您的账户</p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="username-register" className="block text-sm font-medium text-gray-700">
                  用户名 <span className="text-gray-500 text-xs font-normal">(可选)</span>
                </label>
                <input
                  id="username-register"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="请输入用户名（不填则使用邮箱前缀）"
                />
                <p className="mt-1 text-xs text-gray-500">如果不填写，系统将自动使用您的邮箱前缀作为用户名</p>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  邮箱地址 *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="请输入邮箱地址"
                />
              </div>

              <div>
                <label htmlFor="password-register" className="block text-sm font-medium text-gray-700">
                  密码 *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password-register"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请输入密码（至少6位）"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword-register" className="block text-sm font-medium text-gray-700">
                  确认密码 *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword-register"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请再次输入密码"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="pin-register" className="block text-sm font-medium text-gray-700">
                  安全PIN码 * (6位数字)
                </label>
                <input
                  id="pin-register"
                  name="pin"
                  type="text"
                  required
                  maxLength={6}
                  value={formData.pin}
                  onChange={(e) => {
                    // 只允许输入数字
                    const value = e.target.value.replace(/\D/g, '');
                    setFormData({ ...formData, pin: value });
                  }}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="请输入6位数字PIN码"
                />
                <div className="mt-1 space-y-1">
                  <p className="text-xs text-gray-600">
                    <strong>PIN码作用：</strong>用于找回密码时验证您的身份，请妥善保管，不要泄露给他人。
                  </p>
                  <p className="text-xs text-red-600">
                    <strong>重要提示：</strong>PIN码是您找回密码的唯一凭证，请务必牢记并妥善保管。如忘记PIN码，将无法通过此方式找回密码。
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPin-register" className="block text-sm font-medium text-gray-700">
                  确认PIN码 *
                </label>
                <input
                  id="confirmPin-register"
                  name="confirmPin"
                  type="text"
                  required
                  maxLength={6}
                  value={formData.confirmPin}
                  onChange={(e) => {
                    // 只允许输入数字
                    const value = e.target.value.replace(/\D/g, '');
                    setFormData({ ...formData, confirmPin: value });
                  }}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="请再次输入PIN码"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '注册中...' : '注册'}
              </button>
            </div>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                已有账户？点击登录
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
